import { createDrawer, Drawer } from '@nexelem/drawer';
import { useCustomTranslation } from '../../../../../../hooks';
import {
  faCalendarClock,
  faCalendarDay,
  faIndustryWindows,
  faUpRightFromSquare,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { QueueBreakNode } from '../../types';
import { useDateFormatter } from '@nexelem/utils';
import { getTimeFormat } from '../../../../../../utils';
import { Button } from '@nexelem/components';
import { generatePath, Link } from 'react-router-dom';
import { getAbsolutePath, ROUTES } from '../../../../../../routes/config';
import type { ExternalId } from '../../../../../../pages/generic/Products/components/TaskDetailDrawer/components/TaskDetailsDrawerFooter';
import type { QueueView } from '@nexelem/graphne-api';

type NodeBreakDrawerProps = {
  node: QueueBreakNode;
};

const NodeBreakDrawer = ({ node }: NodeBreakDrawerProps) => {
  const { t, tWrapper } = useCustomTranslation('products.productionPlan');
  const dateFormatter = useDateFormatter({ displayWithHours: true });

  const queue = node.queue;

  return (
    <Drawer>
      <Drawer.Header
        title={
          <div className="flex items-center gap-2 text-t24-sb">
            <FontAwesomeIcon className="text-icon-primary" icon={faCalendarClock} />
            <span>{t('common.break')}</span>
          </div>
        }
        onClose={closeNodeBreakDrawer}
      />
      <Drawer.Content>
        <div className="flex flex-col rounded-xs border border-border-container bg-surface-container-lighter pt-5 pb-6">
          <div className="mb-5 flex items-center justify-between pr-2 pl-4">
            <span className="text-t16-b text-text-primary">{t('common.break')}</span>
          </div>
          <div className="px-4">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <span className="py-1 text-t14-sb text-text-secondary-darker">{tWrapper('kanban.queue')}</span>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: `#${queue.area.hex}` }} />
                  <span className="text-s18-spaced text-text-primary">
                    <span className="font-bold wrap-break-word">{queue.name}</span> ({queue.shortName})
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="py-1 text-t14-sb text-text-secondary-darker">{t('common.start')}</span>
                <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
                  <FontAwesomeIcon className="size-5! text-icon-darker" icon={faCalendarDay} />

                  <span>{dateFormatter(node.startDate)}</span>
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="py-1 text-t14-sb text-text-secondary-darker">{t('common.end')}</span>
                <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
                  <FontAwesomeIcon className="size-5! text-icon-darker" icon={faCalendarDay} />
                  <span>{dateFormatter(node.endDate)}</span>
                </span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="py-1 text-t14-sb text-text-secondary-darker">{t('common.duration')}</span>
                <span className="flex items-center gap-2 text-s18-spaced text-text-primary">
                  <FontAwesomeIcon className="size-5! text-icon-darker" icon={faIndustryWindows} />
                  {getTimeFormat((node.endDate.getTime() - node.startDate.getTime()) / 1000)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Drawer.Content>
      <Drawer.Footer>
        <Button
          component={Link}
          StartIcon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
          target="_blank"
          to={pathToStationCalendar(queue)}
          variant="text"
        >
          {tWrapper('goToStationCalendar')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
};

const pathToStationCalendar = (queue: QueueView) => {
  const id = String((queue.externalId as ExternalId).id);

  return getAbsolutePath(generatePath(ROUTES.workstationSettings.details.partial, { id }));
};

export const { toggleNodeBreakDrawer, closeNodeBreakDrawer, useIsOpenNodeBreakDrawer } = createDrawer(NodeBreakDrawer, {
  name: 'NodeBreakDrawer',
  width: 420,
  slot: 'left',
  drawerId: ({ node }) => 'node-break-drawer' + node.id,
});
